import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../lib/api';
import type { Me, MissionNeed, NeedCandidate, NeedDecision, NeedDetail, NeedInput, NeedStatus, Tag, TeamRole } from '../lib/api';
import { navigate } from '../lib/router';
import { daysUntil, fmtDate, fmtDateTime, splitList, titleCase } from '../lib/format';
import { fmtK } from '../lib/money';
import { Empty, Kpi, Modal, PageHead, Panel, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { ScoreBadge } from '../components/matches';

const STATUSES: NeedStatus[] = ['signal', 'validated', 'matched', 'engaged', 'solicited', 'closed'];
const STATUS_KIND: Record<NeedStatus, 'info' | 'warn' | 'ok' | 'muted'> = { signal: 'info', validated: 'warn', matched: 'ok', engaged: 'ok', solicited: 'ok', closed: 'muted' };
const ROLES: TeamRole[] = ['integrator', 'provider', 'research', 'manufacturing', 'test', 'convener', 'sponsor'];

export function MissionNeeds({ params, me }: { params: string[]; me: Me }) {
  return params[0] ? <NeedPage id={params[0]} me={me} /> : <NeedList me={me} />;
}

function NeedList({ me }: { me: Me }) {
  const [data, setData] = useState<{ items: MissionNeed[]; counts: { status: NeedStatus; n: number }[] } | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [status, setStatus] = useState('all');
  const [tag, setTag] = useState('');
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const canEdit = me.role !== 'viewer';

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status !== 'all') p.set('status', status);
    if (tag) p.set('tag', tag);
    if (q) p.set('q', q);
    api<{ items: MissionNeed[]; counts: { status: NeedStatus; n: number }[] }>(`/api/needs?${p}`).then(setData).catch(() => setData({ items: [], counts: [] }));
  }, [status, tag, q]);
  useEffect(load, [load]);
  useEffect(() => { api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([])); }, []);

  const count = (s: NeedStatus) => data?.counts.find((c) => c.status === s)?.n ?? 0;
  return (
    <>
      <PageHead
        title="Mission Needs"
        sub="Government problems and capability gaps captured before a solicitation exists, matched to the ecosystem that could solve them"
        actions={
          <>
            <form className="search" onSubmit={(e: FormEvent) => { e.preventDefault(); setQ(draft.trim()); }} style={{ minWidth: 240 }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Title, problem, sponsor…" />
            </form>
            {canEdit && <button className="primary-btn" onClick={() => setCreating(true)}>+ New need</button>}
          </>
        }
      />
      <div className="kpi-row">
        <Kpi label="Signals" value={count('signal')} hint="unvalidated" />
        <Kpi label="Validated" value={count('validated')} />
        <Kpi label="Matched" value={count('matched')} hint="team forming" />
        <Kpi label="Engaged" value={count('engaged')} />
        <Kpi label="Solicited" value={count('solicited')} hint="an opportunity was released" />
        <Kpi label="Closed" value={count('closed')} />
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${status === 'all' ? 'active' : ''}`} onClick={() => setStatus('all')}>All statuses</button>
        {STATUSES.map((s) => <button key={s} className={`filter-btn ${status === s ? 'active' : ''}`} onClick={() => setStatus(s)}>{titleCase(s)}</button>)}
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${tag === '' ? 'active' : ''}`} onClick={() => setTag('')}>Any technology</button>
        {tags.map((t) => <button key={t.id} className={`filter-btn ${tag === t.id ? 'active' : ''}`} onClick={() => setTag(t.id)}>{t.label}</button>)}
      </div>
      <div className="panel">
        <div className="panel-body table-wrap">
          {!data ? <Empty>Loading…</Empty> : data.items.length === 0 ? (
            <Empty>No mission needs recorded{status !== 'all' || tag || q ? ' for these filters' : ''}.{canEdit && ' Capture the first one with “New need”.'}</Empty>
          ) : (
            <table className="data-table">
              <thead><tr><th>Need</th><th>Status</th><th>Technology</th><th className="num">Required</th><th className="num">Candidates</th><th className="num">Team</th><th className="num">Links</th><th>Updated</th></tr></thead>
              <tbody>
                {data.items.map((n) => (
                  <tr key={n.id} className="clickable" onClick={() => navigate('mission-needs', n.id)}>
                    <td><strong>{n.title}</strong><div className="muted sm">{[n.sponsor_org_name ?? n.sponsor_name, n.mission_area, n.preferred_geography ? `prefers ${n.preferred_geography}` : null].filter(Boolean).join(' · ')}</div></td>
                    <td><Pill kind={STATUS_KIND[n.status]}>{titleCase(n.status)}</Pill></td>
                    <td><TagChips tags={n.technology_tags} all={tags} max={3} /></td>
                    <td className="num">{n.required_capabilities.length}</td>
                    <td className="num">{n.candidates}</td>
                    <td className="num">{n.team > 0 ? <span className="pos">{n.team}</span> : 0}</td>
                    <td className="num">{n.links}</td>
                    <td className="muted">{fmtDate(n.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {creating && <NeedForm tags={tags} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); navigate('mission-needs', id); }} />}
    </>
  );
}

type Draft = { title: string; sponsor_name: string; mission_area: string; status: NeedStatus; problem: string; desired_outcome: string; required_capabilities: string; technical_constraints: string; maturity_required: string; funding_pathways: string; preferred_geography: string; target_date: string; source: string };
const fromDetail = (d?: NeedDetail | null): Draft => ({
  title: d?.title ?? '', sponsor_name: d?.sponsor_name ?? '', mission_area: d?.mission_area ?? '', status: d?.status ?? 'signal', problem: d?.problem ?? '', desired_outcome: d?.desired_outcome ?? '',
  required_capabilities: d?.required_capabilities.join(', ') ?? '', technical_constraints: d?.technical_constraints ?? '', maturity_required: d?.maturity_required ?? '',
  funding_pathways: d?.funding_pathways ?? '', preferred_geography: d?.preferred_geography ?? '', target_date: d?.target_date?.slice(0, 10) ?? '', source: d?.source ?? '',
});

function NeedForm({ tags, existing, onClose, onSaved }: { tags: Tag[]; existing?: NeedDetail | null; onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState<Draft>(() => fromDetail(existing));
  const [picked, setPicked] = useState<string[]>(existing?.technology_tags ?? []);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true); setErr(null);
    const body: NeedInput = {
      title: f.title.trim(), sponsor_name: f.sponsor_name.trim() || undefined, mission_area: f.mission_area.trim() || undefined, status: f.status,
      problem: f.problem.trim() || undefined, desired_outcome: f.desired_outcome.trim() || undefined, technology_tags: picked, required_capabilities: splitList(f.required_capabilities),
      technical_constraints: f.technical_constraints.trim() || undefined, maturity_required: f.maturity_required.trim() || undefined, funding_pathways: f.funding_pathways.trim() || undefined,
      preferred_geography: f.preferred_geography.trim() || undefined, target_date: f.target_date || null, source: f.source.trim() || undefined,
    };
    try {
      if (existing) { await api(`/api/needs/${existing.id}`, { method: 'PATCH', json: body }); onSaved(existing.id); }
      else { const r = await api<{ id: string }>('/api/needs', { method: 'POST', json: body }); onSaved(r.id); }
    } catch (ex) { setErr((ex as Error).message); } finally { setSaving(false); }
  }
  return (
    <Modal title={existing ? 'Edit mission need' : 'New mission need'} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="form-grid">
          <div className="field span-2"><label htmlFor="mn-title">Title</label><input id="mn-title" className="input" required minLength={3} value={f.title} onChange={set('title')} placeholder="What the government needs, in one line" /></div>
          <div className="field"><label htmlFor="mn-sponsor">Sponsor (organization / program)</label><input id="mn-sponsor" className="input" value={f.sponsor_name} onChange={set('sponsor_name')} /></div>
          <div className="field"><label htmlFor="mn-area">Mission area</label><input id="mn-area" className="input" value={f.mission_area} onChange={set('mission_area')} /></div>
          <div className="field span-2"><label htmlFor="mn-problem">Operational or technical problem</label><textarea id="mn-problem" className="textarea" value={f.problem} onChange={set('problem')} /></div>
          <div className="field span-2"><label htmlFor="mn-outcome">Desired capability / outcome</label><textarea id="mn-outcome" className="textarea" value={f.desired_outcome} onChange={set('desired_outcome')} /></div>
          <div className="field span-2"><label>Technology domains <span className="muted">(drives matching)</span></label>
            <div className="tag-list">{tags.map((t) => <button type="button" key={t.id} className={`tag-toggle ${picked.includes(t.id) ? 'on' : ''}`} onClick={() => setPicked(picked.includes(t.id) ? picked.filter((x) => x !== t.id) : [...picked, t.id])}>{t.label}</button>)}</div>
          </div>
          <div className="field span-2"><label htmlFor="mn-req">Required capabilities <span className="muted">(comma separated; team formation checks these off)</span></label><textarea id="mn-req" className="textarea" value={f.required_capabilities} onChange={set('required_capabilities')} placeholder="advanced packaging, rad-hard assembly, known-good-die test" /></div>
          <div className="field"><label htmlFor="mn-con">Known technical constraints</label><input id="mn-con" className="input" value={f.technical_constraints} onChange={set('technical_constraints')} /></div>
          <div className="field"><label htmlFor="mn-trl">Maturity required <span className="muted">(e.g. TRL 6 by FY2028)</span></label><input id="mn-trl" className="input" value={f.maturity_required} onChange={set('maturity_required')} /></div>
          <div className="field"><label htmlFor="mn-fund">Potential acquisition / funding pathways</label><input id="mn-fund" className="input" value={f.funding_pathways} onChange={set('funding_pathways')} /></div>
          <div className="field"><label htmlFor="mn-geo">Preferred geography <span className="muted">(state code)</span></label><input id="mn-geo" className="input" maxLength={10} value={f.preferred_geography} onChange={set('preferred_geography')} placeholder="MA" /></div>
          <div className="field"><label htmlFor="mn-date">Target date</label><input id="mn-date" className="input" type="date" value={f.target_date} onChange={set('target_date')} /></div>
          <div className="field"><label htmlFor="mn-status">Status</label>
            <select id="mn-status" className="select" value={f.status} onChange={set('status')}>{STATUSES.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}</select>
          </div>
          <div className="field span-2"><label htmlFor="mn-src">Source of the requirement</label><input id="mn-src" className="input" value={f.source} onChange={set('source')} placeholder="Industry day remarks, RFI, stakeholder conversation…" /></div>
        </div>
        {err && <div className="error-text">{err}</div>}
        <div className="form-actions"><button type="button" className="ghost-btn" onClick={onClose}>Cancel</button><button type="submit" className="primary-btn" disabled={saving || f.title.trim().length < 3}>{saving ? 'Saving…' : existing ? 'Save changes' : 'Create need'}</button></div>
      </form>
    </Modal>
  );
}

function NeedPage({ id, me }: { id: string; me: Me }) {
  const [d, setD] = useState<NeedDetail | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const canEdit = me.role !== 'viewer';

  const load = useCallback(() => { api<NeedDetail>(`/api/needs/${id}`).then((r) => { setD(r); setErr(null); }).catch((e: Error) => setErr(e.message)); }, [id]);
  useEffect(() => { load(); api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([])); }, [load]);

  async function decide(c: NeedCandidate, decision: NeedDecision, role?: TeamRole) {
    setBusy(c.id);
    try { await api(`/api/needs/${id}/candidates/${c.id}/decision`, { method: 'POST', json: { decision: c.decision === decision ? 'pending' : decision, role: role ?? null } }); load(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }
  async function setRole(c: NeedCandidate, role: TeamRole) {
    try { await api(`/api/needs/${id}/candidates/${c.id}/decision`, { method: 'POST', json: { decision: c.decision, role } }); load(); } catch (e) { setErr((e as Error).message); }
  }
  async function link(target_type: string, target_id: string, relation: string) {
    setBusy(target_id);
    try { await api(`/api/needs/${id}/links`, { method: 'POST', json: { target_type, target_id, relation } }); load(); } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }
  async function unlink(linkId: string) { try { await api(`/api/needs/${id}/links/${linkId}`, { method: 'DELETE' }); load(); } catch (e) { setErr((e as Error).message); } }
  async function setStatus(status: NeedStatus) { try { await api(`/api/needs/${id}`, { method: 'PATCH', json: { status } }); load(); } catch (e) { setErr((e as Error).message); } }
  async function rescore() { setBusy('rescore'); try { await api(`/api/needs/${id}/rescore`, { method: 'POST' }); load(); } finally { setBusy(null); } }
  async function addNote(e: FormEvent) {
    e.preventDefault(); if (!note.trim()) return;
    try { await api(`/api/needs/${id}/notes`, { method: 'POST', json: { body: note.trim() } }); setNote(''); load(); } catch (ex) { setErr((ex as Error).message); }
  }

  if (err && !d) return <><PageHead title="Mission need" /><Empty>{err === 'not_found' ? 'This need does not exist or was removed.' : err}</Empty></>;
  if (!d) return <><PageHead title="Loading…" /><Empty>Loading…</Empty></>;
  const linked = new Set(d.links.map((l) => l.target_id));
  const active = d.candidates.filter((c) => c.decision !== 'pass');
  const rationaleLine = (c: NeedCandidate) => {
    const r = c.rationale; const parts: string[] = [];
    if (r.tags?.length) parts.push(r.tags.map((t) => tags.find((x) => x.id === t.tag_id)?.label ?? t.tag_id).join(', ') + ` +${r.tags.reduce((s, t) => s + t.points, 0)}`);
    if (r.required_covered?.length) parts.push(`covers ${r.required_covered.map((x) => x.phrase).join(', ')} +${r.required_covered.reduce((s, x) => s + x.points, 0)}`);
    if (r.text_hits?.length) parts.push(`mentions ${r.text_hits.map((x) => x.phrase).join(', ')} +${r.text_hits.reduce((s, x) => s + x.points, 0)}`);
    if (r.maturity?.points) parts.push(`TRL ${r.maturity.candidate} vs ${r.maturity.required} ${r.maturity.points > 0 ? '+' : ''}${r.maturity.points}`);
    if (r.geography?.points) parts.push(`${r.geography.candidate} +${r.geography.points}`);
    return parts.join(' · ');
  };

  return (
    <>
      <PageHead
        title={d.title}
        sub={[d.sponsor_org_name ?? d.sponsor_name, d.mission_area, d.preferred_geography ? `prefers ${d.preferred_geography}` : null, d.target_date ? `target ${fmtDate(d.target_date)}` : null].filter(Boolean).join(' · ') || 'Mission need'}
        actions={
          <>
            <Pill kind={STATUS_KIND[d.status]}>{titleCase(d.status)}</Pill>
            {canEdit && <button className="ghost-btn" disabled={busy === 'rescore'} onClick={rescore}>{busy === 'rescore' ? 'Scoring…' : 'Rescore'}</button>}
            {canEdit && <button className="primary-btn" onClick={() => setEditing(true)}>Edit</button>}
            <a className="ghost-btn" href="#/mission-needs">All needs</a>
          </>
        }
      />
      {err && <div className="error-text" style={{ marginBottom: 10 }}>{err}</div>}
      <div className="kpi-row">
        <Kpi label="Candidates ≥ 30" value={active.length} hint={`${d.candidates.filter((c) => c.is_client).length} clients`} />
        <Kpi label="Shortlisted" value={d.candidates.filter((c) => c.decision === 'shortlist').length} />
        <Kpi label="On the team" value={d.candidates.filter((c) => c.decision === 'team').length} />
        <Kpi label="Capability gaps" value={d.team.gaps.length} hint={`of ${d.required_capabilities.length} required`} />
        <Kpi label="Links" value={d.links.length} hint="notices, lines, orgs" />
        <Kpi label="Notes" value={d.notes.length} />
      </div>
      {canEdit && (
        <div className="filter-bar">
          <span className="muted sm" style={{ alignSelf: 'center' }}>Move to:</span>
          {STATUSES.filter((s) => s !== d.status).map((s) => <button key={s} className="filter-btn" onClick={() => setStatus(s)}>{titleCase(s)}</button>)}
        </div>
      )}

      <div className="grid grid-2">
        <Panel title="The need">
          <dl className="kv kv-wide">
            <dt>Problem</dt><dd>{d.problem ?? '—'}</dd>
            <dt>Desired outcome</dt><dd>{d.desired_outcome ?? '—'}</dd>
            <dt>Technology</dt><dd><TagChips tags={d.technology_tags} all={tags} max={12} /></dd>
            <dt>Constraints</dt><dd>{d.technical_constraints ?? '—'}</dd>
            <dt>Maturity</dt><dd>{d.maturity_required ?? '—'}</dd>
            <dt>Funding pathways</dt><dd>{d.funding_pathways ?? '—'}</dd>
            <dt>Source</dt><dd>{d.source ?? '—'}</dd>
          </dl>
        </Panel>
        <Panel title="Team formation" action={<span className="muted sm">Requirement → capabilities → candidates → gap</span>}>
          {d.required_capabilities.length === 0 ? <Empty>Add required capabilities to the need to see coverage and gaps.</Empty> : (
            <>
              {d.team.required.map((r) => (
                <div className="status-line" key={r.capability}>
                  <Pill kind={r.gap ? 'warn' : 'ok'}>{r.gap ? 'Gap' : `${r.covered_by.length} candidate${r.covered_by.length === 1 ? '' : 's'}`}</Pill>
                  <span><strong>{r.capability}</strong>{r.covered_by.length > 0 && <span className="muted sm"> · {r.covered_by.slice(0, 3).map((c) => c.name).join(', ')}{r.covered_by.length > 3 ? ` +${r.covered_by.length - 3}` : ''}</span>}</span>
                </div>
              ))}
              <h3 style={{ fontSize: 12.5, margin: '14px 0 6px' }}>Suggested team</h3>
              <div className="tag-list">
                {d.team.suggested_team.map((t) => (
                  <span key={t.role} className={`tag-toggle ${t.candidate ? 'on' : ''}`} style={{ cursor: 'default' }}>
                    {titleCase(t.role)}: {t.candidate ? `${t.candidate.name} (${t.candidate.score ?? '—'})` : 'no candidate'}
                  </span>
                ))}
              </div>
              {d.team.gaps.length > 0 && <div className="muted sm" style={{ marginTop: 10 }}>Remaining gap: {d.team.gaps.join(', ')}. Add an organization with that capability on the Ecosystem Map, or a client profile.</div>}
            </>
          )}
        </Panel>
      </div>

      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-head"><h2>Candidates <span className="count">{d.candidates.length}</span></h2><span className="muted sm">clients and ecosystem organizations with matching claims</span></div>
        <div className="panel-body table-wrap">
          {d.candidates.length === 0 ? <Empty>No candidate scores against this need yet. Add technology domains and required capabilities, or record capability claims on the Ecosystem Map.</Empty> : (
            <table className="data-table">
              <thead><tr><th>Fit</th><th>Candidate</th><th>Role</th><th>Why</th><th>Decision</th></tr></thead>
              <tbody>
                {d.candidates.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => { location.hash = c.is_client ? `/clients/${c.candidate_id}` : `/ecosystem/id/${c.candidate_id}`; }}>
                    <td><ScoreBadge score={c.score} /></td>
                    <td><strong>{c.name}</strong><div className="muted sm">{c.is_client ? <Pill kind="info">client</Pill> : `${titleCase(c.kind ?? 'organization')}${c.city ? ` · ${c.city}, ${c.state}` : c.state ? ` · ${c.state}` : ''}`}</div></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canEdit ? (
                        <select className="select" style={{ width: 'auto', padding: '4px 26px 4px 8px', fontSize: 12 }} value={c.role ?? c.rationale.role} onChange={(e) => setRole(c, e.target.value as TeamRole)}>
                          {ROLES.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
                        </select>
                      ) : titleCase(c.role ?? c.rationale.role)}
                    </td>
                    <td className="muted sm" style={{ maxWidth: 360 }}>{rationaleLine(c)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canEdit ? (
                        <span className="decision-btns">
                          <button className={`decision-btn watch ${c.decision === 'shortlist' ? 'on' : ''}`} disabled={busy === c.id} onClick={() => decide(c, 'shortlist')}>Shortlist</button>
                          <button className={`decision-btn pursue ${c.decision === 'team' ? 'on' : ''}`} disabled={busy === c.id} onClick={() => decide(c, 'team')}>Team</button>
                          <button className={`decision-btn pass ${c.decision === 'pass' ? 'on' : ''}`} disabled={busy === c.id} onClick={() => decide(c, 'pass')}>Pass</button>
                        </span>
                      ) : <Pill kind={c.decision === 'team' ? 'ok' : c.decision === 'shortlist' ? 'warn' : 'muted'}>{titleCase(c.decision)}</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid grid-2">
        <Panel title="Open notices in these technology areas" action={<span className="muted sm">has an opportunity appeared for this need?</span>}>
          {d.related_solicitations.length === 0 ? <Empty>No open notices share a technology tag with this need.</Empty> : d.related_solicitations.map((s) => {
            const days = daysUntil(s.response_due_at);
            return (
              <div className="alert" key={s.id}>
                <div className="alert-body">
                  <a className="alert-link" href={`#/solicitations/id/${s.id}`}><strong>{s.title}</strong></a>
                  <div className="muted sm">{titleCase(s.notice_type)} · {s.issuing_org_path?.split('.').slice(-1)[0] ?? ''}{s.response_due_at ? ` · due ${fmtDate(s.response_due_at)}${days !== null && days >= 0 ? ` (${days}d)` : ''}` : ''}</div>
                </div>
                {canEdit && (linked.has(s.id) ? <Pill kind="ok">linked</Pill> : <button className="ghost-btn ack-btn" disabled={busy === s.id} onClick={() => link('solicitation', s.id, 'solicitation for this need')}>Link</button>)}
              </div>
            );
          })}
        </Panel>
        <Panel title="Budget lines in these technology areas" action={<span className="muted sm">where the money could come from</span>}>
          {d.related_programs.length === 0 ? <Empty>No budget lines share a technology tag with this need.</Empty> : d.related_programs.map((p) => (
            <div className="alert" key={p.id}>
              <div className="alert-body">
                <a className="alert-link" href={`#/budget/id/${p.id}`}><strong>{p.pe_number ?? p.line_item}</strong> {p.title}</a>
                <div className="muted sm">{p.service}{p.latest ? ` · FY${p.latest.fiscal_year} ${fmtK(p.latest.amount)}` : ''}</div>
              </div>
              {canEdit && (linked.has(p.id) ? <Pill kind="ok">linked</Pill> : <button className="ghost-btn ack-btn" disabled={busy === p.id} onClick={() => link('program', p.id, 'funding line')}>Link</button>)}
            </div>
          ))}
        </Panel>
      </div>

      <div className="grid grid-2">
        <Panel title={`Links (${d.links.length})`}>
          {d.links.length === 0 ? <div className="muted sm">Nothing linked yet. Link the notices, budget lines, organizations and events this need connects to.</div> : d.links.map((l) => (
            <div className="version" key={l.id}>
              <time>{fmtDate(l.created_at)}</time>
              <span style={{ flex: 1 }}><Pill kind="muted">{titleCase(l.target_type)}</Pill> {l.label ?? l.target_id}{l.relation ? <span className="muted sm"> · {l.relation}</span> : null}</span>
              {canEdit && <button className="ghost-btn ack-btn" onClick={() => unlink(l.id)} title="Unlink">✕</button>}
            </div>
          ))}
        </Panel>
        <Panel title={`Notes (${d.notes.length})`}>
          {d.notes.map((n) => <div className="note" key={n.id}><div><span className="tag new" style={{ marginLeft: 0, marginRight: 8 }}>{titleCase(n.kind).toUpperCase()}</span>{n.body}</div><div className="meta">{n.author ?? 'Unknown'} · {fmtDateTime(n.created_at)}</div></div>)}
          {canEdit && (
            <form onSubmit={addNote} style={{ marginTop: 10 }}>
              <div className="field"><label htmlFor="mn-note">Add a note</label><textarea id="mn-note" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Stakeholder intelligence, validation, ecosystem response strategy…" /></div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={!note.trim()}>Save note</button></div>
            </form>
          )}
        </Panel>
      </div>

      {editing && <NeedForm tags={tags} existing={d} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </>
  );
}
